import React, { useEffect, useState } from "react";
import ParagraphTag from "../../reusebale/Paragraph";
import Breadcrumbs from "../../reusebale/breadcrumbs";
import NoData from "../../reusebale/noData";
import MyLoader from "../../reusebale/myLoader";
import { RotateCcw, AlertCircle, AlertTriangle } from "lucide-react";
import BaseUrl from "../../../constants/BaseUrl";
import { useSelector } from "react-redux";
import RetryTransactionPopUp from "../../popup/retryTransactionPopUp";
import { ShowError, ShowSuccess } from "../../../utils/ToasFunction";

const OutstandingBalancesTable = ({ onBack, landlordId }) => {
  const { loginSuccess } = useSelector((state) => state.auth);
  const [failedTransactions, setFailedTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [retryTransaction, setRetryTransaction] = useState(null);
  const [retryLoading, setRetryLoading] = useState(false);

  const fetchFailedTransactions = () => {
    if (!landlordId) return;
    setLoading(true);

    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);

    fetch(
      `${BaseUrl}api/payment-transaction/failed/landlord/${landlordId}`,
      { method: "GET", headers: myHeaders, redirect: "follow" }
    )
      .then((res) => res.json())
      .then((result) => {
        if (result.status === "success") {
          setFailedTransactions(result.data || []);
        }
      })
      .catch((err) => console.error("Error fetching failed transactions:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchFailedTransactions();
  }, [landlordId]);

  const handleRetryConfirm = async () => {
    if (!retryTransaction) return;
    setRetryLoading(true);
    try {
      const myHeaders = new Headers();
      myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);
      myHeaders.append("Content-Type", "application/json");

      const response = await fetch(`${BaseUrl}api/payment-transaction/retry`, {
        method: "POST",
        headers: myHeaders,
        body: JSON.stringify({ transactionId: retryTransaction._id }),
        redirect: "follow",
      });

      const result = await response.json();
      if (result.status === "success") {
        ShowSuccess("Transaction retry initiated successfully");
        setRetryTransaction(null);
        fetchFailedTransactions();
      } else {
        ShowError(result.message || "Failed to retry transaction");
      }
    } catch (error) {
      console.error("Error retrying transaction:", error);
      ShowError("Something went wrong while retrying the transaction");
    } finally {
      setRetryLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "$0.00";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatSchedulePeriod = (fromDate, toDate) => {
    if (!fromDate || !toDate) return "-";
    const fmt = (d) => {
      const date = new Date(d + "T00:00:00");
      return date.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" });
    };
    return `${fmt(fromDate)} - ${fmt(toDate)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit",
    });
  };

  const getLeaseStatusBadge = (status) => {
    const s = status?.toLowerCase() || "";
    if (s === "active") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-popinsMedium bg-green-100 text-green-700">
          Active
        </span>
      );
    }
    if (s === "pendingpayment") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-popinsMedium bg-yellow-100 text-yellow-700">
          Pending Payment
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-popinsMedium bg-gray-100 text-gray-700 capitalize">
        {status || "-"}
      </span>
    );
  };

  return (
    <div className="mt-5 w-full">
      <Breadcrumbs
        classes="text-darkColor font-popinsSemiBold text-xl"
        subheading="Outstanding Balances"
        onBack={onBack}
        recordId="Lease with Outstanding Balances"
      />

      <div className="bg-white w-full rounded-lg mt-5">
        <div className="w-[97%] mx-auto h-full py-5 relative">
          {loading && (
            <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex justify-center items-center z-10 rounded-lg min-h-[200px]">
              <MyLoader />
            </div>
          )}

          {/* Column Headers */}
          <div className="flex border-b-[1px] border-darkColor/40 pb-2">
            <div className="w-[20%]">
              <ParagraphTag
                content="Tenants"
                classes="text-darkColor/40 font-popinsMedium text-sm"
              />
            </div>
            <div className="w-[12%] pl-3">
              <ParagraphTag
                content="Outstanding Amount"
                classes="text-darkColor/40 font-popinsMedium text-sm"
              />
            </div>
            <div className="w-[15%] pl-3">
              <ParagraphTag
                content="Rent Period"
                classes="text-darkColor/40 font-popinsMedium text-sm"
              />
            </div>
            <div className="w-[13%] pl-3">
              <ParagraphTag
                content="Purpose"
                classes="text-darkColor/40 font-popinsMedium text-sm"
              />
            </div>
            <div className="w-[15%] pl-3">
              <ParagraphTag
                content="Lease Period"
                classes="text-darkColor/40 font-popinsMedium text-sm"
              />
            </div>
            <div className="w-[9%] pl-3">
              <ParagraphTag
                content="Frequency"
                classes="text-darkColor/40 font-popinsMedium text-sm"
              />
            </div>
            <div className="w-[8%] pl-3">
              <ParagraphTag
                content="Lease Status"
                classes="text-darkColor/40 font-popinsMedium text-sm"
              />
            </div>
            <div className="w-[8%] text-end">
              <ParagraphTag
                content="Quick Actions"
                classes="text-darkColor/40 font-popinsMedium text-sm"
              />
            </div>
          </div>

          {/* Rows */}
          <div>
            {failedTransactions.map((txn) => {
              const rentAmount =
                (txn.amount || 0) - (txn.security_deposit || 0);
              return (
                <div
                  key={txn._id}
                  className="flex items-center bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-lg p-3 mb-2 hover:shadow-md transition-shadow"
                >
                  {/* Tenant */}
                  <div className="w-[20%] flex items-center">
                    <div className="w-8 h-8 flex-shrink-0 bg-red-100 rounded-full flex items-center justify-center mr-2">
                      <AlertCircle className="w-4 h-4 text-red-500" />
                    </div>
                    <div className="min-w-0">
                      <ParagraphTag
                        content={txn.tenantName || "-"}
                        classes="text-darkColor font-popinsSemiBold text-sm truncate"
                      />
                      <ParagraphTag
                        content={
                          txn.unitName
                            ? `${txn.propertyAddress} · ${txn.unitName}`
                            : txn.propertyAddress || "-"
                        }
                        classes="text-darkColor/40 font-popinsMedium text-xs truncate"
                      />
                    </div>
                  </div>

                  {/* Outstanding Amount */}
                  <div className="w-[12%] pl-3">
                    <ParagraphTag
                      content={formatCurrency(rentAmount)}
                      classes="text-red-600 font-popinsSemiBold text-sm"
                    />
                  </div>

                  {/* Rent Period */}
                  <div className="w-[15%] pl-3">
                    <ParagraphTag
                      content={formatSchedulePeriod(txn.scheduleFromDate, txn.scheduleToDate)}
                      classes="text-darkColor font-popinsMedium text-xs"
                    />
                  </div>

                  {/* Purpose */}
                  <div className="w-[13%] pl-3">
                    <ParagraphTag
                      content={txn.purpose || "Scheduled Rent"}
                      classes="text-darkColor font-popinsMedium text-sm"
                    />
                  </div>

                  {/* Lease Period */}
                  <div className="w-[15%] pl-3">
                    <ParagraphTag
                      content={`${formatDate(txn.leaseStartDate)} - ${formatDate(txn.leaseEndDate)}`}
                      classes="text-darkColor font-popinsMedium text-xs"
                    />
                  </div>

                  {/* Frequency */}
                  <div className="w-[9%] pl-3">
                    <ParagraphTag
                      content={txn.rentFrequency || "-"}
                      classes="text-darkColor font-popinsMedium text-sm capitalize"
                    />
                  </div>

                  {/* Lease Status */}
                  <div className="w-[8%] pl-3">
                    {getLeaseStatusBadge(txn.leaseStatus)}
                  </div>

                  {/* Quick Actions */}
                  <div className="w-[8%] flex justify-end">
                    {txn.retryInProgress ? (
                      <div className="relative group flex justify-center items-center bg-amber-100 w-8 h-8 rounded-lg">
                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                        <div className="absolute bottom-full mb-2 right-0 hidden group-hover:block bg-themeColor text-white text-xs font-popinsMedium px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap z-20">
                          A transaction for this period is already in progress
                          <div className="absolute top-full right-3 w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[5px] border-t-themeColor"></div>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setRetryTransaction(txn)}
                        className="flex justify-center items-center bg-red-500 w-8 h-8 rounded-lg cursor-pointer hover:bg-red-600 transition-colors"
                        title="Retry Transaction"
                      >
                        <RotateCcw color="#FFFFFF" size={14} strokeWidth={2} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            {!loading && failedTransactions.length === 0 && (
              <div className="w-full mt-8 flex justify-center">
                <NoData />
              </div>
            )}
          </div>
        </div>
      </div>

      {retryTransaction && (
        <RetryTransactionPopUp
          transaction={retryTransaction}
          loading={retryLoading}
          onConfirm={handleRetryConfirm}
          onClose={() => setRetryTransaction(null)}
        />
      )}
    </div>
  );
};

export default OutstandingBalancesTable;
